/* eslint-disable react-hooks/rules-of-hooks */
import {
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Radio,
    RadioGroup,
    useTheme,
} from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { convertToDisplayName } from '../helpers';
import { FormSelectOption, GenericFormComponentProps } from '../types';
import { useFormSectionContext } from '../FormSection/FormSection';
import { useFormState } from 'hooks/useFormState';
import { useEffect } from 'react';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface FormRadioOption<TValue = string | number>
    extends FormSelectOption {
    /** Custom element to display after the option. */
    afterChildren?: JSX.Element;
}

interface FormRadioProps extends GenericFormComponentProps {
    isNumber?: boolean;
    isTextArea?: boolean;
    options: FormRadioOption[];
    horizontal?: boolean;
    /** Styling class name for radio option. */
    optionClassName?: string;
    /** Custom element (e.g., perhaps a help icon with tooltip etc.) placed to the right of the radio field label. */
    labelAdornment?: JSX.Element;
}

/**
 * simple form radio input
 * @param {string} field - field to track with react-hook-form
 * @param {FormRadioOption[]} options - list of options to pick from
 * @param {string} label - optional label override
 * @param {boolean} horizontal - optional param to arrange radio group in a horizontal layout. Default is vertical layout.
 * @param {string} optionClassName - optional class name for styling for radio option.
 * @param {JSX.Element} labelAdornment - Optional custom element (e.g., perhaps a help icon with tooltip etc.) placed to the right of the radio field label.
 * @returns FormRadio component
 */
export function FormRadio({
    field,
    options,
    label,
    disabled,
    horizontal = false,
    optionClassName,
    labelAdornment,
}: FormRadioProps) {
    const { control, getFieldState, setValue } = useFormContext();
    const { setFormSectionError } = useFormState();
    const { formSection } = useFormSectionContext();
    const theme = useTheme();

    const watchedField = getFieldState(field);

    useEffect(() => {
        setFormSectionError({
            section: formSection,
            field: { name: field, error: !!watchedField.error },
        });
    }, [field, formSection, setFormSectionError, watchedField.error]);

    label = label || convertToDisplayName(field);

    return (
        <Controller
            name={field}
            control={control}
            render={({ field: fieldProps, fieldState: { error } }) => {
                return (
                    <FormControl
                        sx={
                            error
                                ? {
                                      color: theme.palette.error.main,
                                  }
                                : undefined
                        }
                        disabled={disabled}
                        data-testid={`${field}-radio-form-input`}
                    >
                        <FormLabel
                            id="radio-group-form-label"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {label}
                            {labelAdornment}
                        </FormLabel>
                        <RadioGroup
                            defaultValue={options[0]}
                            aria-labelledby="radio-group-form-label"
                            row={horizontal}
                        >
                            {options.map((option) => {
                                const label =
                                    option.name ??
                                    convertToDisplayName(option.value);

                                const notDisabled =
                                    !disabled && !option.disabled;

                                return (
                                    <Box
                                        key={option.value}
                                        className={optionClassName}
                                    >
                                        <FormControlLabel
                                            value={option.value}
                                            control={
                                                <Radio
                                                    checked={
                                                        fieldProps.value ===
                                                        option.value
                                                    }
                                                    disabled={option.disabled}
                                                    onChange={
                                                        fieldProps.onChange
                                                    }
                                                />
                                            }
                                            label={label}
                                            disabled={option.disabled}
                                            onClick={() =>
                                                notDisabled &&
                                                setValue(field, option.value)
                                            }
                                        />
                                        {option.afterChildren
                                            ? option.afterChildren
                                            : null}
                                    </Box>
                                );
                            })}
                        </RadioGroup>
                        {error && (
                            <FormHelperText
                                sx={{
                                    color: theme.palette.error.main,
                                }}
                            >
                                {error.message}
                            </FormHelperText>
                        )}
                    </FormControl>
                );
            }}
        />
    );
}
